import { take } from 'rxjs/operators';
import { Injectable, Type } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { BehaviorSubject, Observable } from 'rxjs';
import { NotificationNode, NotificationConfig, NotificationRoomInfo } from './notification-node';
import { AlertType, AlertComponent } from '../dialogs/components/alert/alert.component';
import { BackgroundJobsService } from '../background-jobs/background-jobs.service';
import { BackgroundJobComponent } from '../background-jobs/components/background-job/background-job.component';
import { BackgroundJob } from '../background-jobs/background-job';
import { JobManagementModel } from '@app/origami-api/services/job-management.service';
import { PopupService, PopupRef, PopupSettings } from '@progress/kendo-angular-popup';
import { DomainSessionInfo, RoomSessionInfo } from '@app/session/session.service';
import { getServerConfig } from '@app/origami-api/server-config';
import { getErrorDescription } from '@app/origami-api/utils/error-utils';
import { StandardErrorDialogOptions } from '@app/services';
import { EmittedBrowsedDocsInfo } from '@app/pages/main/pages/startup-agency-submission/components/packages-documents/submission-document-upload/submission-document-upload.component';

export const popupMaxZIndexCssClass = 'ti-kendo-popup-max-z-index';

class RoomInfo {
  static id: number;
  static roomName: string;

  static setRoomInfo(id: number, name: string): void {
    this.id = id;
    this.roomName = name;
  }
}

@Injectable()
export class NotificationsService {
  change: Observable<NotificationsService>;

  get items() {
    return this.root.items;
  }
  get totalCount() {
    return this.root.totalCount;
  }
  get totalCountForRoom() {
    return this.root.getTotalCountForRoom(this.roomId);
  }
  get hotItemsCount() {
    return this.root.hotItemsCount;
  }
  get hotItemsCountForRoom() {
    return this.root.geHotItemsCountForRoom(this.roomId);
  }

  private readonly backgroundJobAlertTimeout = 5000;
  private get generalAlertTimeout() {
    return getServerConfig().notificationTimeout;
  }

  private readonly strings = tiStrings.app;
  private root: NotificationNode;
  private selectedNode: NotificationNode;
  private sourceChange = new BehaviorSubject<NotificationsService>(this);
  private popupRef: PopupRef;
  private roomId: number;
  private roomName: string;

  constructor(
    private popupService: PopupService,
    private backgroundJobsService: BackgroundJobsService,
  ) {
    this.change = this.sourceChange.asObservable();
    this.createRoot();
  }

  createRoot() {
    this.root = NotificationNode.create('Root', true, this.strings.misc.allNotificationGroup);
    this.root.add(this.strings.misc.backgroundJobNotificationsGroup, null, true);
  }

  setRoomInfo(room: RoomSessionInfo | DomainSessionInfo) {
    this.roomId = room ? room.id : null;
    this.roomName = room && room.info ? room.info.SiteName : null;
    room && RoomInfo.setRoomInfo(room.id, room.info?.SiteName);
  }

  startIgnoringPopups(shownNotificationNode: NotificationNode) {
    this.selectedNode = shownNotificationNode;
    this.closePopup();
  }

  stopIgnoringPopups() {
    this.selectedNode = null;
  }

  showSuccess(
    message: string,
    source?: string,
    timeout?: number,
    notSave?: boolean,
    asHTML?: boolean,
    subgroupTitle?: string,
  ): Promise<any> {
    return this.showAlert(message, source, 'success', timeout, notSave, asHTML, subgroupTitle);
  }

  showWarning(
    message: number | string,
    source?: string,
    timeout?: number,
    notSave?: boolean,
    asHTML?: boolean,
    subgroupTitle?: string,
    detailsTemplateInfo?: EmittedBrowsedDocsInfo,
  ): Promise<any> {
    source = source || this.strings.misc.warning;
    return this.showAlert(message, source, 'warning', timeout, notSave, asHTML, subgroupTitle, detailsTemplateInfo);
  }

  showInfo(
    message: number | string,
    source?: string,
    timeout?: number,
    notSave?: boolean,
    asHTML?: boolean,
    subgroupTitle?: string,
  ): Promise<any> {
    source = source || this.strings.misc.info;
    return this.showAlert(message, source, 'info', timeout, notSave, asHTML, subgroupTitle);
  }

  showError(
    message: number | string,
    source?: string,
    timeout?: number,
    notSave?: boolean,
    asHTML?: boolean,
    subgroupTitle?: string,
    detailsTemplateInfo?: EmittedBrowsedDocsInfo,
  ): Promise<any> {
    source = source || this.strings.misc.error;
    return this.showAlert(message, source, 'error', timeout, notSave, asHTML, subgroupTitle, detailsTemplateInfo);
  }

  showBanner(html: SafeHtml, customClassName?: string): void {
    this.closePopup();

    const settings: Partial<PopupSettings> = {
      popupAlign: { horizontal: 'right', vertical: 'top' },
      collision: { horizontal: 'fit', vertical: 'fit' },
      positionMode: 'fixed',
      popupClass: 'banner-content',
    };

    this.popupRef = this.createPopupRef(AlertComponent, settings);
    const alertInstance = this.popupRef.content.instance;

    alertInstance.html = html;
    alertInstance.type = null;
    alertInstance.closable = true;
    alertInstance.isBanner = true;
    alertInstance.customClassName = customClassName;
    alertInstance.close.subscribe(() => this.closePopup());
  }

  async addJob(options: {
    siteId: number;
    jobId: string;
    jobTitle: string;
    showResultAfterFinish?: boolean;
    showJobResultSuccess?: (jobResult: JobManagementModel) => void;
    showJobResultError?: (jobResult: JobManagementModel) => void;
    jobResultSuccessParser?: (jobResult: JobManagementModel) => string;
    jobResultErrorParser?: (jobResult: JobManagementModel) => string;
    jobStatusMessageParser?: (job: BackgroundJob) => string;
    roomSpecific?: boolean;
    standardErrorDialogOptions?: StandardErrorDialogOptions;
    hideCancel?: boolean;
    hideResult?: boolean;
    useActionAsTitle?: boolean;
    pollImmediately?: boolean;
    customCompletionDetailsPattern?: string;
  }): Promise<any> {
    const job = this.backgroundJobsService.add(options.siteId, options.jobId, options.jobTitle, this.roomName, {
      pollImmediately: options.pollImmediately,
    });
    const initBackgroundJobComponent = (
      instance: BackgroundJobComponent,
      jobData: BackgroundJob,
      popupRef?: PopupRef,
    ) => {
      instance.showJobResultSuccess = options.showJobResultSuccess;
      instance.showJobResultError = options.showJobResultError;
      instance.jobResultSuccessParser = options.jobResultSuccessParser;
      instance.jobResultErrorParser = options.jobResultErrorParser;
      instance.jobStatusMessageParser = options.jobStatusMessageParser;
      instance.standardErrorDialogOptions = options.standardErrorDialogOptions;
      instance.showResultAfterFinish = popupRef && options.showResultAfterFinish;
      instance.closable = !!popupRef;
      instance.cancelable = !options.hideCancel;
      instance.hideResult = !!options.hideResult;
      instance.useActionAsTitle = !!options.useActionAsTitle;
      instance.data = jobData;
      instance.customCompletionDetailsPattern = options?.customCompletionDetailsPattern;

      const jobChangeSubs = job.change.subscribe((updatedJob: BackgroundJob) => {
        if (instance) {
          instance.data = updatedJob;
        }
      });

      if (popupRef) {
        const closeJob = () => {
          if (instance) {
            jobChangeSubs.unsubscribe();
            instance.close.unsubscribe();
            this.closePopup(popupRef);
            instance = null;
          }
        };

        instance.close.pipe(take(1)).subscribe(closeJob);
        setTimeout(() => closeJob, this.backgroundJobAlertTimeout);
      }
    };

    return this.addNotification(
      this.strings.misc.backgroundJobNotificationsGroup,
      options.jobTitle,
      false,
      {
        hot: true,
        component: BackgroundJobComponent,
        colorSchema: 'dark',
        data: job,
        updater: initBackgroundJobComponent,
      },
      {
        roomId: this.roomId || RoomInfo.id,
        roomName: this.roomName || RoomInfo.roomName,
        roomSpecific: options.roomSpecific !== undefined ? options.roomSpecific : false,
      },
    ).then(() => {
      const popupRef = this.createPopupRef(BackgroundJobComponent);
      initBackgroundJobComponent(popupRef.content.instance, job, popupRef);
    });
  }

  closeAllPopups() {
    this.closePopup();
  }

  read(node: NotificationNode) {
    if (node) {
      node.hot = false;
    }
    this.onChange();
  }

  delete(node: NotificationNode) {
    node.delete();
    this.onChange();
  }

  deleteAll() {
    this.root.deleteAll();
    this.onChange();
  }

  private async showAlert(
    message: number | string,
    title: string,
    alertType: AlertType,
    timeout?: number,
    notSave?: boolean,
    asHTML?: boolean,
    subgroupTitle?: string,
    detailsTemplateInfo?: EmittedBrowsedDocsInfo,
  ): Promise<any> {
    let text: string;

    if (typeof message === 'number') {
      text = getErrorDescription(message) || getErrorDescription('unknown');
      if (alertType === 'error' || alertType === 'warning') {
        // console.error(text);
      }
    } else {
      text = message;
    }

    return this.addNotification(
      this.strings.misc.actionNotificationsGroup,
      text,
      notSave,
      {
        hot: true,
        component: AlertComponent,
        colorSchema: alertType === 'info' ? 'dark' : 'white',
        updater: (instance: AlertComponent, data: any) => {
          instance.title = title;
          if (asHTML) {
            instance.html = text;
          } else {
            instance.text = text;
          }
          instance.type = alertType;
          instance.closable = false;
          instance.detailsTemplateInfo = detailsTemplateInfo;
        },
      },
      {
        roomId: this.roomId || RoomInfo.id,
        roomName: this.roomName || RoomInfo.roomName,
        roomSpecific: true,
      },
      subgroupTitle,
    ).then(() => {
      const popupRef = this.createPopupRef(AlertComponent);
      const popupInstance: AlertComponent = popupRef.content.instance;
      popupInstance.detailsTemplateInfo = detailsTemplateInfo;
      popupInstance.title = title;
      if (asHTML) {
        popupInstance.html = text;
      } else {
        popupInstance.text = text;
      }
      popupInstance.type = alertType;
      popupInstance.close.pipe(take(1)).subscribe(() => this.closePopup(popupRef));

      setTimeout(() => {
        popupInstance.close.unsubscribe();
        this.closePopup(popupRef);
      }, timeout || this.generalAlertTimeout);
    });
  }

  private addNotification<TdComponent, TData>(
    groupTitle: string,
    title: string,
    ignoreStore: boolean,
    options: NotificationConfig<TdComponent, TData>,
    roomInfo: NotificationRoomInfo,
    subGroupTitle: string = title,
  ): Promise<{ node: NotificationNode | null; show: boolean }> {
    return new Promise((resolve) => {
      if (ignoreStore) {
        return resolve({ node: null, show: true });
      }

      let group = this.root.items.find((item) => item.title === groupTitle);
      if (!group) {
        group = this.root.add(groupTitle, null, true);
      }

      let subgroup = group.items.find((subgr) => {
        const sameTitle = subgr.title === subGroupTitle;
        const sameRoomId = subgr.roomInfo && subgr.roomInfo.roomId === roomInfo.roomId;
        const isSpecificForRoom = (subgr.roomInfo && subgr.roomInfo.roomSpecific) || roomInfo.roomSpecific;
        return sameTitle && (sameRoomId || !isSpecificForRoom);
      });
      if (!subgroup) {
        const subGroupRoomInfo = {
          roomId: roomInfo.roomSpecific ? roomInfo.roomId : null,
          roomName: roomInfo.roomSpecific ? roomInfo.roomName : null,
          roomSpecific: roomInfo.roomSpecific,
        };
        subgroup = group.add(subGroupTitle, null, null, subGroupRoomInfo);
      } else {
        group.moveChildToTop(subgroup.id);
      }

      const node = subgroup.add(title, options, true, roomInfo);

      this.onChange();

      if (!node.data) {
        return resolve({ node: node, show: true });
      }

      node.data.initialized.subscribe(() => resolve({ node: node, show: false }));

      if (this.selectedNode !== group && this.selectedNode !== this.items[0]) {
        return resolve({ node: null, show: true });
      }
    });
  }

  private onChange() {
    this.sourceChange.next(this);
  }

  private createPopupRef(content: Type<any>, settings: Partial<PopupSettings> = {}): PopupRef {
    this.closePopup();

    return (this.popupRef = this.popupService.open({
      ...settings,
      content: content,
      animate: false,
      popupAlign: settings.popupAlign || { horizontal: 'left', vertical: 'top' },
      popupClass: popupMaxZIndexCssClass,
      offset: {
        top: settings?.offset?.top || 50,
        left: settings?.offset?.left || document.documentElement.clientWidth - 1012,
      },
    }));
  }

  private closePopup(popupRef?: PopupRef): void {
    if (!popupRef || popupRef === this.popupRef) {
      this.popupRef && this.popupRef.close();
      this.popupRef = null;
    }
  }
}
